import { Auth } from "@aws-amplify/auth";
import { Amplify } from "@aws-amplify/core";
import React, { createContext, useContext, useEffect, useState } from "react";
import { AwsConfigAuth } from "../config/auth";

Amplify.configure({ Auth: AwsConfigAuth });

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  token: string;
  checkSession: () => Promise<boolean>;
  signIn: (username: string, password: string) => Promise<Result>;
  completeNewPassword: (result: any, password: string) => Promise<Result>;
  signOut: () => Promise<Result>;
  signUp: (data: User) => Promise<Result>;
  confirmSignUp: (username: string, code: string, password: string) => Promise<Result>;
  resendSignUp: (username: string) => Promise<Result>;
  forgotPassword: (username: string) => Promise<Result>;
  resetPassword: (username: string, code: string, password: string) => Promise<Result>;
  updateUser: (data: User) => Promise<Result>;
  verifyEmailUpdate: (code: string) => Promise<Result>;
}

interface Result {
  success: boolean;
  message: string;
}

interface User {
  username: string;
  password: string;
  given_name: string;
  family_name: string;
  phone_number: string;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((result: any) => {
        setToken(result.signInUserSession.accessToken.jwtToken)
        setIsAuthenticated(true);
        setIsLoading(false);
      })
      .catch(() => {
        setToken("");
        setIsAuthenticated(false);
        setIsLoading(false);
      });
  }, []);

  const checkSession = async (): Promise<boolean> => {
    try {
      const session = await Auth.currentSession();
      setToken(session.getAccessToken().getJwtToken());
      return true;
    } catch {
      return false;
    }
  };

  const signIn = async (username: string, password: string) => {
    try {
      const result = await Auth.signIn(username, password);

      if (result?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return { success: false, message: 'NEW_PASSWORD_REQUIRED', result: result };
      } else {
        const tokens = await Auth.currentSession();
        setToken(tokens.getAccessToken().getJwtToken());
        setIsAuthenticated(true);

        return { success: true, message: "" };
      }
  } catch (error: any) {
      setIsAuthenticated(false);

      return {
        success: false,
        message: error.message,
      };
    }
  };

  const completeNewPassword = async (result: any, newPassword: string) => {
    try {
      await Auth.completeNewPassword(result, newPassword);

      const tokens = await Auth.currentSession();
      setToken(tokens.getAccessToken().getJwtToken());
      setIsAuthenticated(true);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsAuthenticated(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const signOut = async () => {
    try {
      await Auth.signOut();
      setToken("");
      setIsAuthenticated(false);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  };

  const signUp = async (data: User) => {
    try {
      const result: any = await Auth.signUp({
        username: data.username,
        password: data.password,
        attributes: {
          given_name: data.given_name,
          family_name: data.family_name,
          phone_number: data.phone_number,
        },
        autoSignIn: {
          enabled: true,
        }
      });

      if(!result.userConfirmed){
        setIsLoading(false);
        return { success: true, message: "" };
      }

      setToken(result.signInUserSession.accessToken.jwtToken);
      setIsAuthenticated(true);
      setIsLoading(false);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const confirmSignUp = async (username: string, code: string, password: string) => {
    try {
      const result = await Auth.confirmSignUp(username, code);

      if (result === 'SUCCESS') {
        const user = await Auth.signIn(username, password);
        setToken(user.signInUserSession.accessToken.jwtToken);
        setIsAuthenticated(true);
        setIsLoading(false);

        return { success: true, message: "" };
      }

      setIsLoading(false);

      return { success: false, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const resendSignUp = async (username: string) => {
    try {
      await Auth.resendSignUp(username);
      setIsLoading(false);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const forgotPassword = async (username: string) => {
    try {
      await Auth.forgotPassword(username);
      setIsLoading(false);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const resetPassword = async (username: string, code: string, password: string) => {
    try {
      await Auth.forgotPasswordSubmit(username, code, password);
      setIsLoading(false);

      return { success: true, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const updateUser = async (data: User) => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result: any = await Auth.updateUserAttributes(user, {
        email: data.username,
        given_name: data.given_name,
        family_name: data.family_name,
        phone_number: data.phone_number,
      });

      setIsLoading(false);
      if (result === 'SUCCESS') {
        return { success: true, message: "" };
      }

      return { success: true, message: result };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }

  const verifyEmailUpdate = async (code: string) => {
    try {
      const result = await Auth.verifyCurrentUserAttributeSubmit('email', code);

      if (result === 'SUCCESS') {
        setIsLoading(false);

        return { success: true, message: "" };
      }

      setIsLoading(false);

      return { success: false, message: "" };
    } catch (error: any) {
      setIsLoading(false);

      return {
        success: false,
        message: error.message,
      };
    }
  }


  return {
    isLoading,
    isAuthenticated,
    token,
    checkSession,
    signIn,
    completeNewPassword,
    signOut,
    signUp,
    confirmSignUp,
    resendSignUp,
    forgotPassword,
    resetPassword,
    updateUser,
    verifyEmailUpdate,
  };
};